<template>
  <saved-report-workspace
    v-if="mode !== 'create'"
    ref="savedWorkspace"
    :report-code="reportCode"
    :mode="mode"
    :document-id="documentId"
    :history-id="historyId"
  />
  <app-page v-else class="report-details" labelledby="page-title" full-width>
    <page-header :title="report.name" :description="report.description">
      <template #status>
        <q-badge outline :color="headerStatus.color">{{ headerStatus.label }}</q-badge>
      </template>
      <template #metadata>
        Текущая организация{{ currentOrgId ? ` #${currentOrgId}` : ' не выбрана' }}
      </template>
      <template #actions>
        <q-btn
          flat
          no-caps
          color="primary"
          icon="arrow_back"
          label="К списку"
          :to="catalogRoute"
        />
      </template>
    </page-header>

    <page-section title="Параметры">
      <report-filters
        :model-value="filters"
        :report="report"
        :loading="loading"
        :current-org-id="currentOrgId"
        @update:model-value="onFiltersUpdated"
        @reset="onFiltersUpdated"
        @generate="generateReport"
      />
    </page-section>

    <page-section
      title="Результат"
      :description="resultDescription"
      data-testid="report-result-section"
    >
      <template #actions>
        <div class="report-details__artifact-actions">
          <report-artifact-actions
            :payload="artifactPayload"
            :org-id="currentOrgId"
            :disabled="!hasResult || stale || loading"
          />
          <q-btn
            v-if="supportsXml"
            outline
            no-caps
            color="primary"
            icon="code"
            label="Скачать XML ФНС"
            :disable="taxExportDisabled"
            data-testid="tax-xml-open"
            @click="taxXmlOpen = true"
          />
        </div>
      </template>

      <q-banner v-if="stale" dense rounded class="report-details__notice bg-warning text-dark">
        {{ taxReportChanged ? 'Данные отчёта изменились. Нажмите «Сформировать» перед выгрузкой XML.' : 'Фильтры изменены. Нажмите «Сформировать», чтобы обновить отчет.' }}
      </q-banner>
      <q-banner v-else-if="partial" dense rounded class="report-details__notice bg-warning text-dark">
        Отчет построен частично. Проверьте диагностику и источники данных.
      </q-banner>

      <app-state
        v-if="loading && !hasResult"
        state="loading"
        title="Формируем отчет"
        message="Собираем данные и проверяем источники."
      />
      <app-state
        v-else-if="state === 'error' && !hasResult"
        state="error"
        title="Не удалось сформировать отчет"
        :message="errorMessage"
        @retry="generateReport"
      />
      <app-state
        v-else-if="state === 'idle'"
        state="empty"
        title="Отчет еще не сформирован"
        message="Проверьте период и нажмите «Сформировать»."
      />
      <app-state
        v-else-if="resultIsEmpty"
        state="filtered-empty"
        title="За выбранный период данных нет"
        message="Измените период или параметры отчета и сформируйте его повторно."
        @reset="resetFromEmpty"
      />

      <template v-else-if="hasResult">
        <q-linear-progress v-if="loading" indeterminate color="primary" class="q-mb-md" />
        <q-tabs
          v-model="activeTab"
          dense
          align="left"
          mobile-arrows
          outside-arrows
          class="report-details__tabs text-primary"
        >
          <q-tab name="report" label="Отчет" />
          <q-tab name="diagnostics" :label="`Диагностика · ${diagnostics.length}`" />
          <q-tab name="sources" :label="`Источники · ${sourceCount}`" />
          <q-tab name="export" label="Экспорт" />
        </q-tabs>

        <q-tab-panels v-model="activeTab" animated>
      <q-tab-panel name="report" class="q-pa-none">
        <div v-if="report.kind === 'accounting'">
          <div v-if="reportData && reportData.organization.name" class="text-caption text-grey-7 q-mt-md">
            Организация: {{ reportData.organization.name }}
            <span v-if="reportData.organization.inn">, ИНН {{ reportData.organization.inn }}</span>
          </div>
          <report-table
            :rows="reportRows"
            :value-columns="reportValueColumns"
            :measure-label="measureLabel"
          />
        </div>

        <div v-else-if="report.kind === 'vat'">
          <vat-declaration-view
            :summary="vatSummary"
            :sections="vatSections"
            :incoming-count="incomingRows.length"
            :outgoing-count="outgoingRows.length"
          />
          <document-book-table
            title="Раздел 8 — книга покупок"
            :rows="incomingRows"
            :columns="bookColumnsFor('purchase_book')"
          />
          <document-book-table
            title="Раздел 9 — книга продаж"
            :rows="outgoingRows"
            :columns="bookColumnsFor('sales_book')"
          />
        </div>

        <div v-else>
          <document-book-table
            :title="report.name"
            :rows="bookRows"
            :columns="bookColumnsFor(report.code)"
            :totals="bookData ? bookData.totals : {}"
          />
        </div>
      </q-tab-panel>

      <q-tab-panel name="diagnostics" class="q-pa-none">
        <report-diagnostics
          :diagnostics="diagnostics"
          :supported="diagnosticsSupported"
        />
      </q-tab-panel>

      <q-tab-panel name="sources" class="q-pa-none">
        <report-sources
          :documents="sourceDocuments"
          :transfers="sourceTransfers"
        />
      </q-tab-panel>

      <q-tab-panel name="export" class="q-pa-none q-pt-md" data-testid="report-export-panel">
        <report-artifact-actions
          :payload="artifactPayload"
          :org-id="currentOrgId"
          :disabled="!hasResult || stale || loading"
          stacked
        />
        <q-btn
          v-if="supportsXml"
          class="q-mt-sm"
          outline
          no-caps
          color="primary"
          icon="code"
          label="Скачать XML ФНС"
          :disable="taxExportDisabled"
          @click="taxXmlOpen = true"
        />
      </q-tab-panel>
        </q-tab-panels>
      </template>
    </page-section>
    <tax-xml-export-dialog
      v-if="supportsXml"
      v-model="taxXmlOpen"
      :report-code="report.code"
      :report-name="report.name"
      :payload="taxXmlPayload"
      :source-documents="sourceDocuments"
      :organization="taxOrganization"
      :report-revision="generationRevision"
      :disabled="taxExportDisabled"
      @report-changed="onTaxReportChanged"
    />
  </app-page>
</template>

<script>
import moment from 'moment'
import { normalizeDate as normalizeDisplayDate } from '../../../services/dateFormat'
import AppPage from '../../../components/ui/AppPage.vue'
import AppState from '../../../components/ui/AppState.vue'
import PageHeader from '../../../components/ui/PageHeader.vue'
import PageSection from '../../../components/ui/PageSection.vue'
import ReportFilters from './components/ReportFilters.vue'
import ReportTable from './components/ReportTable.vue'
import ReportDiagnostics from './components/ReportDiagnostics.vue'
import ReportSources from './components/ReportSources.vue'
import VatDeclarationView from './components/VatDeclarationView.vue'
import DocumentBookTable from './components/DocumentBookTable.vue'
import TaxXmlExportDialog from './components/TaxXmlExportDialog.vue'
import ReportArtifactActions from '../../../components/documents/ReportArtifactActions.vue'
import SavedReportWorkspace from './SavedReportWorkspace.vue'
import {
  BOOK_COLUMNS,
  getReportByCode,
  reportSupportsXmlExport,
  REPORT_CODES,
  REPORT_FAMILIES
} from './utils/reportMetadata'
import {
  buildReportArtifactPayload,
  getAccountingReport,
  getBookReport,
  getVatReport
} from './api/reportsApi'
import {
  normalizeAccountingReport,
  normalizeBookReport,
  normalizeVatReport
} from './utils/reportNormalizer'

const measureOption = { label: 'руб.', value: 'rub' }
const quarterOption = { label: 'Квартал', value: 'quarter' }

export default {
  name: 'ReportDetailsPage',
  components: {
    AppPage,
    AppState,
    PageHeader,
    PageSection,
    ReportFilters,
    ReportTable,
    ReportDiagnostics,
    ReportArtifactActions,
    ReportSources,
    SavedReportWorkspace,
    VatDeclarationView,
    DocumentBookTable,
    TaxXmlExportDialog
  },
  props: {
    reportCode: {
      type: String,
      required: true
    },
    mode: {
      type: String,
      default: 'create',
      validator: (value) => ['create', 'read', 'edit', 'print'].includes(value)
    },
    documentId: {
      type: [String, Number],
      default: null
    },
    historyId: {
      type: [String, Number],
      default: 'last'
    }
  },
  data() {
    return {
      filters: this.initialFilters(),
      state: 'idle',
      loading: false,
      generationRevision: 0,
      stale: false,
      taxXmlOpen: false,
      taxReportChanged: false,
      taxOrganization: {},
      errorMessage: '',
      activeTab: 'report',
      reportData: null,
      vatData: null,
      bookData: null,
      bookRows: []
    }
  },
  computed: {
    report() {
      return getReportByCode(this.reportCode) || getReportByCode(REPORT_CODES.BALANCE)
    },
    currentOrgId() {
      return this.$store.state.current_org_id || (this.connection && this.connection.org_id)
    },
    supportsXml() {
      return reportSupportsXmlExport(this.report.code)
    },
    taxExportDisabled() {
      const permissions = this.$store.state.permissions || {}
      return !this.hasResult || this.state !== 'success' || this.stale || this.loading || this.$store.state.connection_status === 'offline' ||
        !(permissions.open_fin_documents === true || permissions.edit_fin_documents === true)
    },
    taxXmlPayload() {
      const payload = {
        org_id: Number(this.currentOrgId), report_code: this.report.code,
        period_start: this.artifactPayload.period_start, period_end: this.artifactPayload.period_end,
        unit: 'rub', currency: 'RUB'
      }
      const metadata = (this.vatData || this.bookData)?.metadata || {}
      if (metadata.fns_source_hash) payload.expected_source_hash = metadata.fns_source_hash
      return payload
    },
    catalogRoute() {
      const routes = {
        [REPORT_FAMILIES.ACCOUNTING]: 'reports_accounting_group',
        [REPORT_FAMILIES.VAT]: 'reports_vat_group',
        [REPORT_FAMILIES.SPECIAL]: 'reports_special_group'
      }
      return { name: routes[this.report.family] || 'reports_list' }
    },
    hasResult() {
      if (this.report.kind === 'accounting') {
        return this.reportData !== null
      }
      if (this.report.kind === 'vat') {
        return this.vatData !== null
      }
      return this.bookData !== null
    },
    partial() {
      return Boolean(this.reportData && this.reportData.partial)
    },
    resultIsEmpty() {
      if (!this.hasResult) return false
      if (this.report.kind === 'accounting') return this.reportRows.length === 0
      if (this.report.kind === 'book') return this.bookRows.length === 0
      return false
    },
    sourceCount() {
      return this.sourceDocuments.length + this.sourceTransfers.length
    },
    resultDescription() {
      if (this.stale) return 'Параметры изменены — результат нужно обновить.'
      if (this.loading) return 'Данные обновляются.'
      if (this.hasResult) return `${this.filters.start} — ${this.filters.end}`
      return 'После формирования здесь появятся данные, диагностика и источники.'
    },
    headerStatus() {
      if (this.loading) return { color: 'info', label: 'Формируется' }
      if (this.state === 'error') return { color: 'negative', label: 'Ошибка' }
      if (this.stale) return { color: 'warning', label: 'Требует обновления' }
      if (this.hasResult) return { color: 'positive', label: 'Сформирован' }
      return { color: 'primary', label: 'Не сформирован' }
    },
    reportRows() {
      return this.reportData ? this.reportData.rows : []
    },
    reportValueColumns() {
      return this.reportData ? this.reportData.valueColumns : []
    },
    measureLabel() {
      return (this.reportData && this.reportData.measureLabel) ||
        (this.filters.measure && this.filters.measure.label) ||
        ''
    },
    vatSummary() {
      return this.vatData ? this.vatData.summary : {}
    },
    vatSections() {
      return this.vatData ? this.vatData.sections : {}
    },
    incomingRows() {
      return this.vatData ? this.vatData.incomingRows : []
    },
    outgoingRows() {
      return this.vatData ? this.vatData.outgoingRows : []
    },
    diagnostics() {
      if (this.report.kind === 'accounting' && this.reportData) {
        return this.reportData.diagnostics
      }
      if (this.report.kind === 'vat' && this.vatData) {
        return this.vatData.diagnostics
      }
      if (this.report.kind === 'book' && this.bookData) {
        return this.bookData.diagnostics
      }
      return []
    },
    diagnosticsSupported() {
      if (this.report.kind === 'accounting' && this.reportData) {
        return this.reportData.diagnosticsSupported
      }
      if (this.report.kind === 'vat' && this.vatData) {
        return this.vatData.diagnosticsSupported
      }
      if (this.report.kind === 'book' && this.bookData) {
        return this.bookData.diagnosticsSupported
      }
      return false
    },
    sourceDocuments() {
      if (this.reportData) {
        return this.reportData.sourceDocuments
      }
      if (this.bookData) {
        return this.bookData.sourceDocuments
      }
      if (this.report.kind === 'book') {
        return this.bookRows
      }
      if (this.report.kind === 'vat') {
        return (this.vatData && this.vatData.sourceDocuments) || this.incomingRows.concat(this.outgoingRows)
      }
      return []
    },
    sourceTransfers() {
      if (this.reportData) {
        return this.reportData.sourceTransfers
      }
      if (this.bookData) {
        return this.bookData.sourceTransfers
      }
      return []
    },
    artifactPayload() {
      return buildReportArtifactPayload(this.connection, this.report.code, this.filters)
    }
  },
  watch: {
    currentOrgId() {
      this.taxXmlOpen = false
      this.resetFromEmpty()
    },
    reportCode() {
      this.taxXmlOpen = false
      this.resetFromEmpty()
    }
  },
  mounted() {
    if (this.mode === 'create') this.syncQuery()
  },
  beforeRouteLeave(to, from, next) {
    const workspace = this.$refs.savedWorkspace
    if (workspace && workspace.isDirty) workspace.requestDirtyLeave(next)
    else next()
  },
  beforeUnmount() { this.generationRevision += 1 },
  methods: {
    initialFilters() {
      const storePeriod = this.$store.state.filter_period || {}
      const query = this.$route.query || {}
      return {
        start: this.normalizeDate(query.start || storePeriod.from || moment().startOf('quarter')),
        end: this.normalizeDate(query.end || storePeriod.to || moment().endOf('quarter')),
        periodPreset: query.period ? { label: this.periodLabel(query.period), value: query.period } : quarterOption,
        measure: measureOption
      }
    },
    normalizeDate(value) {
      return normalizeDisplayDate(value)
    },
    periodLabel(value) {
      const labels = {
        month: 'Месяц',
        quarter: 'Квартал',
        halfyear: 'Полугодие',
        year: 'Год',
        custom: 'Произвольный период'
      }
      return labels[value] || labels.quarter
    },
    syncQuery() {
      if (this.mode !== 'create') return
      const query = {
        ...this.$route.query,
        start: this.filters.start,
        end: this.filters.end,
        period: this.filters.periodPreset && this.filters.periodPreset.value
      }
      this.$router.replace({ query }).catch(() => {})
    },
    onFiltersUpdated(next) {
      this.generationRevision += 1
      this.loading = false
      this.filters = next
      this.$store.commit('updateFilterPeriod', {
        from: next.start,
        to: next.end
      })
      if (this.hasResult) {
        this.stale = true
      }
      this.syncQuery()
    },
    onTaxReportChanged() {
      this.taxReportChanged = true
      this.stale = true
    },
    resetFromEmpty() {
      this.generationRevision += 1
      this.loading = false
      this.stale = false
      this.state = 'idle'
      this.reportData = null
      this.vatData = null
      this.bookData = null
      this.bookRows = []
      this.taxReportChanged = false
      this.taxOrganization = {}
    },
    bookColumnsFor(code) {
      return BOOK_COLUMNS[code] || []
    },
    async generateReport() {
      const revision = ++this.generationRevision
      this.loading = true
      this.errorMessage = ''
      this.activeTab = 'report'
      try {
        if (this.report.kind === 'accounting') {
          const response = await getAccountingReport(this.connection, this.emitter, this.report.code, this.filters)
          if (revision !== this.generationRevision) return
          this.reportData = normalizeAccountingReport(this.report.code, response)
          this.vatData = null
          this.bookData = null
          this.bookRows = []
        } else if (this.report.kind === 'vat') {
          const raw = await getVatReport(this.connection, this.emitter, this.filters)
          if (revision !== this.generationRevision) return
          this.vatData = normalizeVatReport(raw)
          this.taxOrganization = raw.organization || {}
          this.reportData = null
          this.bookData = null
          this.bookRows = []
        } else {
          const response = await getBookReport(this.connection, this.emitter, this.report.code, this.filters)
          if (revision !== this.generationRevision) return
          this.bookData = normalizeBookReport(this.report.code, response)
          this.taxOrganization = response.organization || {}
          this.bookRows = this.bookData.rows
          this.reportData = null
          this.vatData = null
        }
        this.state = 'success'
        this.stale = false
        this.taxReportChanged = false
      } catch (error) {
        if (revision !== this.generationRevision) return
        this.errorMessage = error.message || 'Не удалось сформировать отчет'
        this.state = 'error'
      } finally {
        if (revision === this.generationRevision) this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.report-details { min-width: 0; max-width: 100%; overflow-x: clip; }
.report-details__tabs { max-width: 100%; }
.report-details__artifact-actions { display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: flex-end; gap: var(--sid-space-2); max-width: min(100%, 44rem); }
.report-details__notice { margin-bottom: var(--sid-space-4); }
@media (max-width: 599px) {
  .report-details__artifact-actions { justify-content: stretch; }
  .report-details__artifact-actions > :deep(*) { width: 100%; }
}
</style>
